<template>
  <div class="max-w-7xl mx-auto px-4 py-8">
    <Breadcrumbs :items="[{ label: 'Produse' }]" />

    <div class="mb-4 hidden items-end justify-between gap-4 lg:mb-6 lg:flex">
      <h1
        class="text-2xl font-bold tracking-tight text-highlighted md:text-3xl"
      >
        Produse
      </h1>
      <p v-if="pagination.total" class="shrink-0 text-sm text-muted">
        {{ products.length }} din {{ pagination.total }} produse
      </p>
    </div>

    <AdvantagesBanner class="mb-6" />

    <ProductMobileToolbar
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      @update="onFilterUpdate"
    />

    <ProductFilterChips
      v-if="activeFilterCount > 0"
      class="lg:hidden"
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      scrollable
      @remove="onChipRemove"
    />

    <div
      class="lg:hidden -mx-4 mb-4 flex items-center justify-between bg-muted px-4 py-2.5"
    >
      <span class="font-semibold text-highlighted">Produse</span>
      <span v-if="pagination.total" class="text-sm text-toned">
        {{ pagination.total }} produse
      </span>
    </div>

    <ProductFilterChips
      class="hidden lg:block"
      :filters="filters"
      :brands="productsStore.brands"
      :attributes="productsStore.attributes"
      @remove="onChipRemove"
    />

    <div class="grid grid-cols-1 gap-8 lg:grid-cols-4">
      <aside class="hidden lg:col-span-1 lg:block">
        <ProductFilters :filters="filters" @update="onFilterUpdate" />
      </aside>

      <div class="lg:col-span-3">
        <ProductGrid
          :products="products"
          :loading="loading || !productsFetched"
          :loading-more="loadingMore"
        />

        <div
          v-if="hasMoreProducts"
          ref="loadMoreTrigger"
          class="flex flex-col items-center justify-center gap-2 py-8"
        >
          <UIcon
            v-if="loadingMore"
            name="i-heroicons-arrow-path"
            class="h-6 w-6 animate-spin text-brand-600"
          />
          <p v-if="loadingMore" class="text-sm text-muted">
            Se încarcă mai multe produse...
          </p>
        </div>

        <p
          v-else-if="products.length > 0"
          class="py-8 text-center text-sm text-dimmed"
        >
          Ai văzut toate produsele
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductFilterState } from "~/types";
import {
  countActiveProductFilters,
  parseProductFiltersFromQuery,
  productFiltersToQuery,
} from "~/utils/productFilters";

const route = useRoute();
const router = useRouter();
const productsStore = useProductsStore();
const { productsDefaultSort } = useSiteSettings();

const products = computed(() => productsStore.products);
const loading = computed(() => productsStore.loading);
const loadingMore = computed(() => productsStore.loadingMore);
const productsFetched = computed(() => productsStore.productsFetched);
const hasMoreProducts = computed(() => productsStore.hasMoreProducts);
const pagination = computed(() => productsStore.pagination);
const filters = computed(() => productsStore.filters);
const activeFilterCount = computed(() =>
  countActiveProductFilters(filters.value),
);

const loadMoreTrigger = ref<HTMLElement | null>(null);

const siteUrl = useRuntimeConfig().public.siteUrl as string;

useSeo({
  title: "Produse",
  description:
    "Descoperă gama completă de produse. Filtrează după atribute, preț și disponibilitate.",
  jsonLd: siteBreadcrumbJsonLd(
    [{ name: "Produse", url: `${siteUrl}/produse` }],
    siteUrl,
  ),
});

useIntersectionObserver(
  loadMoreTrigger,
  ([entry]) => {
    if (entry?.isIntersecting) {
      productsStore.loadMoreProducts();
    }
  },
  { rootMargin: "240px" },
);

async function loadProducts(params?: ProductFilterState) {
  await productsStore.fetchProducts({ ...params, page: 1 });
}

function onFilterUpdate(newFilters: ProductFilterState) {
  const query = productFiltersToQuery(
    { ...newFilters, page: 1 },
    productsDefaultSort.value,
  );
  router.push({ query });
  loadProducts(newFilters);
}

function onChipRemove(key: string) {
  const nextFilters: ProductFilterState = { ...filters.value, page: 1 };

  if (key.startsWith("brand-")) {
    const brandId = Number(key.replace("brand-", ""));
    nextFilters.brandIds = (nextFilters.brandIds || []).filter(
      (id) => id !== brandId,
    );
    if (!nextFilters.brandIds.length) {
      nextFilters.brandIds = undefined;
      nextFilters.models = undefined;
      productsStore.fetchModels();
    }
  } else if (key.startsWith("model-")) {
    const model = decodeURIComponent(key.replace("model-", ""));
    nextFilters.models = (nextFilters.models || []).filter(
      (item) => item !== model,
    );
    if (!nextFilters.models.length) nextFilters.models = undefined;
  } else if (key.startsWith("attr-")) {
    const valueId = Number(key.split("-").pop());
    nextFilters.attributeValueIds = (
      nextFilters.attributeValueIds || []
    ).filter((id) => id !== valueId);
    if (!nextFilters.attributeValueIds.length)
      nextFilters.attributeValueIds = undefined;
  } else if (key.startsWith("color-")) {
    const color = decodeURIComponent(key.replace("color-", ""));
    nextFilters.colors = (nextFilters.colors || []).filter(
      (item) => item !== color,
    );
    if (!nextFilters.colors.length) nextFilters.colors = undefined;
  } else if (key.startsWith("storage-")) {
    const storage = decodeURIComponent(key.replace("storage-", ""));
    nextFilters.storages = (nextFilters.storages || []).filter(
      (item) => item !== storage,
    );
    if (!nextFilters.storages.length) nextFilters.storages = undefined;
  } else if (key === "inStock") {
    nextFilters.inStock = false;
  } else if (key.startsWith("availability-")) {
    const statusId = Number(key.replace("availability-", ""));
    nextFilters.availabilityStatusIds = (nextFilters.availabilityStatusIds || []).filter(
      (id) => id !== statusId,
    );
    if (!nextFilters.availabilityStatusIds.length) nextFilters.availabilityStatusIds = undefined;
  } else {
    nextFilters[key as keyof ProductFilterState] = undefined as never;
  }

  onFilterUpdate(nextFilters);
}

async function initProductsPage() {
  const queryFilters = parseProductFiltersFromQuery(
    route.query as Record<string, unknown>,
    productsDefaultSort.value,
  );

  if (import.meta.client && route.query.page) {
    const query = productFiltersToQuery(
      { ...queryFilters, page: 1 },
      productsDefaultSort.value,
    );
    await router.replace({ query });
  }

  await Promise.all([
    productsStore.attributes.length
      ? Promise.resolve()
      : productsStore.fetchAttributes(),
    loadProducts(queryFilters),
  ]);
}

await initProductsPage();
</script>
